import React, { useRef, useEffect } from "react";
import { useUser } from "@services/UserContext";

interface Template {
  id: number;
  name: string;
  description: string;
  created_at: string;
  creator?: { first_name: string; last_name: string; email: string };
}

interface TemplateCardProps {
  template: Template;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onCardClick: (id: number) => void;
  dropdownOpen: number | null;
  setDropdownOpen: (id: number | null) => void;
  noModify?: boolean; // Optional prop to disable edit/delete
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
  onCardClick,
  dropdownOpen,
  setDropdownOpen,
  noModify = false, // Default to false if not provided
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { user } = useUser();

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevents triggering card click
    setDropdownOpen(dropdownOpen === template.id ? null : template.id);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(null);
    }
  };

  // Attach event listener when dropdown is open
  useEffect(() => {
    if (dropdownOpen === template.id) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(template.id);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(template.id);
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString)
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      })
      .replace(",", " -")
      .replace(/\//g, ".");
  };

  const isCreator = user?.email == template.creator?.email;
  const allowEdit = isCreator || user?.is_superuser;

  return (
    <div
      className="py-3 px-3 bg-gray-50 rounded-lg border border-gray-300 flex flex-col hover:bg-gray-100 cursor-pointer relative hover:shadow transition duration-200 ease-in-out"
      onClick={() => onCardClick(template.id)}
    >
      {/* Date */}
      <p className="text-[0.78rem] 2xl:text-sm text-gray-400 mb-4">
        {formatDateTime(template.created_at)}
      </p>

      {/* Title */}
      <h2 className="font-semibold text-gray-900 text-base 2xl:text-lg mb-2 text-ellipsis line-clamp-1">
        {template.name}
      </h2>

      {/* Description */}
      <p className="text-[0.78rem] 2xl:text-sm text-gray-700 mb-2 overflow-hidden line-clamp-2 h-[2.25rem] 2xl:h-[2.5rem]">
        {template.description}
      </p>
      {/* Creator */}
      <p className="text-[0.78rem] 2xl:text-sm text-gray-400 mt-1.5 2xl:mt-3">
        Created by{" "}
        {isCreator ? (
          <span>you</span>
        ) : (
          <span>
            {template.creator?.first_name} {template.creator?.last_name}
          </span>
        )}
      </p>

      {/* Dropdown Menu */}
      {allowEdit && (
        <div className="absolute top-3 right-2" ref={dropdownRef}>
          {!noModify && (
            <button
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={handleDropdownClick}
              aria-label="dropdown-trigger"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="2xl:h-5 2xl:w-5 h-4 w-4"
                viewBox="0 0 20 20"
                fill="#0357C9"
              >
                <path d="M10 4a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
          )}
          {dropdownOpen === template.id && (
            <div className="absolute right-0 mt-1 w-40 2xl:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 text-sm 2xl:text-base">
              <button
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                onClick={handleEditClick}
              >
                Edit
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateCard;
